import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import TransText from "./TransText";
import { PRIMARY_NAV_ITEMS } from "../content/siteNavigation";

// --- (No changes to styled-components or variants) ---
const sidebarVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { delayChildren: 0.3, staggerChildren: 0.2 },
  },
};
const linkVariants = {
  hidden: { x: 50, opacity: 0 },
  visible: { x: 0, opacity: 1, transition: { ease: "easeOut", duration: 0.5 } },
};
const SidebarContainer = styled(motion.div)`
  position: fixed;
  z-index: 4;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: clamp(1rem, 1.8vw, 1.8rem);
  top: clamp(1.75rem, 4vh, 3rem);
  right: clamp(1.75rem, 3vw, 3.5rem);
  transform: none;
  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 0;
    top: auto;
    bottom: calc(1rem + var(--safe-bottom));
    right: 0;
    left: 0;
    transform: none;
    width: 100%;
    min-height: var(--mobile-nav-height);
    padding-bottom: var(--safe-bottom);
    pointer-events: none; /* let clicks pass through empty space */
  }
`;
const SidebarLink = styled(motion.a)`
  color: #fff;
  text-decoration: none;
  font-family: var(--font-body);
  font-size: clamp(1.08rem, 1.18vw, 1.32rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  word-spacing: 0.08em;
  line-height: 1;
  max-width: none;
  white-space: nowrap;
  text-align: right;
  text-shadow: 0px 2px 8px rgba(0, 0, 0, 0.8);
  position: relative;
  transition: color 0.2s ease-in-out, transform .2s ease;
  --mx: 50%;
  --my: 50%;
  /* moving light */
  &::before{
    content:"";
    position:absolute; inset:-10px;
    background: radial-gradient(120px 60px at var(--mx) var(--my),
      rgba(255,255,255,.18), rgba(255,255,255,0) 60%);
    filter: blur(10px);
    opacity: 0; pointer-events:none;
    transition: opacity .18s ease;
  }
  /* underline shimmer */
  &::after{
    content:"";
    position:absolute; left: 14%; right: 86%; bottom: -4px; height: 2px;
    background: linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.9), rgba(255,255,255,0));
    transition: right .34s cubic-bezier(.22,.61,.36,1);
  }
  &:hover { color: #eaeaea; }
  &:hover::before { opacity: 1; }
  &:hover::after { right: 14%; }
  &:focus-visible{
    outline: none;
    text-decoration: underline;
    text-underline-offset: 6px;
  }
  @media (max-width: 768px) {
    font-size: 1.05rem;
    max-width: none;
    white-space: nowrap;
  }
  @media (max-width: 480px) {
    font-size: 0.95rem;
  }
  @media (hover: none){
    &::before{ opacity: 0 !important; }
  }
  pointer-events: auto;
`;
// ---

const Label = styled(motion.span)`
  display: inline-block;
  transition: transform .2s ease, letter-spacing .2s ease, text-shadow .2s ease;
  ${SidebarLink}:hover &{
    transform: translateY(-2px);
    letter-spacing: .02em;
    text-shadow: 0 6px 24px rgba(255,255,255,.25);
  }
`;
const Sidebar = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <SidebarContainer
      variants={sidebarVariants}
      initial="hidden"
      animate="visible"
    >
      <ScrollWrapper>
        {PRIMARY_NAV_ITEMS.map((link) => (
          <SidebarLink
            key={link.key}
            href={link.to}
            variants={linkVariants}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => {
              e.preventDefault();
              onNavigate(link.to);
            }}
          >
            <Label>
              <TransText text={t(link.labelKey)} />
            </Label>
          </SidebarLink>
        ))}
      </ScrollWrapper>
    </SidebarContainer>
  );
};

const ScrollWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: clamp(1rem, 1.8vw, 1.8rem);

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 2rem;
    overflow-x: auto;
    padding: 0 2rem;
    width: 100%;
    justify-content: flex-start;
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
    
    /* Fade mask */
    mask-image: linear-gradient(to right, transparent, black 10%, black 90%, transparent);
  }
`;



export default Sidebar;
